<script setup>
import { riskAssessmentSection } from "@/services/constant-services";
import ANButton from "@/components/common/AnButton.vue";
import { useRouter } from "vue-router";

const router = useRouter();
const sectionData = riskAssessmentSection;

const navigateToAssessment = () => {
  router.push('/risk-assessment');
};
</script>

<template>
  <section id="risk-assessment" class="risk-assessment-section">
    <div class="section-container">
      <div class="risk-content">
        <div class="risk-text-content">
          <h2 class="section-title">{{ sectionData.title }}</h2>
          <p class="section-subtitle">{{ sectionData.subtitle }}</p>

          <div class="features-list">
            <div
              v-for="feature in sectionData.features"
              :key="feature"
              class="feature-item"
            >
              <span class="feature-icon">✓</span>
              <span class="feature-text">{{ feature }}</span>
            </div>
          </div>

          <ANButton
            :label="sectionData.buttonText"
            @click="navigateToAssessment"
            class="assessment-cta-button"
          />
        </div>

        <div class="risk-visual-content">
          <div class="risk-illustration">
            <div class="risk-meter">
              <div class="meter-track">
                <div class="meter-fill conservative"></div>
                <div class="meter-fill moderate"></div>
                <div class="meter-fill aggressive"></div>
              </div>
              <div class="meter-labels">
                <span class="label conservative-label">Conservative</span>
                <span class="label moderate-label">Moderate</span>
                <span class="label aggressive-label">Aggressive</span>
              </div>
            </div>
            <p class="illustration-text">Find your ideal risk-return balance</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.risk-assessment-section {
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  padding: 80px 0;
  color: var(--an-white);
  font-family: "Satoshi-Variable", sans-serif;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.risk-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.risk-text-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  color: var(--an-white);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-subtitle {
  font-size: 1.2rem;
  line-height: 1.6;
  opacity: 0.95;
  margin: 0;
  color: var(--an-white);
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.feature-icon {
  width: 24px;
  height: 24px;
  background: var(--an-white);
  color: var(--an-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.feature-text {
  font-size: 1rem;
  opacity: 0.95;
  color: var(--an-white);
}

.assessment-cta-button {
  align-self: flex-start;
  padding: 16px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 10px;
  background: var(--an-white);
  color: var(--an-primary);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.assessment-cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  background: var(--an-white2);
}

.risk-visual-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.risk-illustration {
  text-align: center;
}

.risk-meter {
  margin-bottom: 20px;
}

.meter-track {
  display: flex;
  height: 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-bottom: 16px;
}

.meter-fill {
  flex: 1;
  height: 100%;
}

.conservative {
  background: var(--an-info); /* Blue instead of green to avoid conflict */
}

.moderate {
  background: var(--an-warning);
}

.aggressive {
  background: var(--an-error);
}

.meter-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  opacity: 0.9;
  color: var(--an-white);
  font-weight: 500;
}

.illustration-text {
  font-size: 1.1rem;
  margin-top: 20px;
  opacity: 0.95;
  color: var(--an-white);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 968px) {
  .risk-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }

  .section-title {
    font-size: 2rem;
  }

  .assessment-cta-button {
    align-self: center;
  }
}

@media (max-width: 768px) {
  .risk-assessment-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-subtitle {
    font-size: 1.1rem;
  }
}
</style>
